// src/components/calendar/CalendarViews/WeekView.jsx - FIXED: Simplified with proper event positioning
import React, { useState, useEffect, useCallback } from 'react';
import { Spinner } from '../../ui/spinner';

// ✅ KEEP: Import our shadcn week components
import WeekViewHeader from '../../ui/calendar/week/WeekViewHeader/WeekViewHeader';
import WeekViewTimeRow from '../../ui/calendar/week/WeekViewTimeRow/WeekViewTimeRow';
import WeekViewEventCard from '../../ui/calendar/week/WeekViewEventCard/WeekViewEventCard';

const WeekView = ({
  currentDate,
  previewEvent,
  onTimeSlotClick,
  onEventClick,
  isAdmin,
  showEventPanel,
  partnerMode = false,

  // Accept events data as props instead of useEvents hook
  events = [],      // Renamed from calendarEvents for clarity
  loading = false,  // Loading state from parent
  error = null,      // Error state from parent

  // creation mode props
  isInCreationMode = false,

  availabilityDates,
  viewingPartnerAvailability,

  // Time range configuration
  timeRange = { start: 8, end: 18 },  // Default: 8 AM - 6 PM

  // Block mode props
  isBlockMode = false,
  persistedSlots = [],
  onSlotsChanged,

  // Selected event for detail panel
  selectedEventId = null
}) => {

  // Drag state for block mode
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartSlot, setDragStartSlot] = useState(null); // { dateStr, hour }
  const [currentDragSlots, setCurrentDragSlots] = useState([]);

  // Business hours configuration - now from props
  const BUSINESS_HOURS = {
    start: timeRange.start,
    end: timeRange.end,
  };

  // Helper: Date object → "YYYY-MM-DD" using ISO string (consistent with WeekViewTimeRow)
  const toDateStr = (date) => date.toISOString().split('T')[0];

  // Block mode: document mouseup handler — handles click (toggle) and drag (additive)
  useEffect(() => {
    if (!isBlockMode) return;

    const handleMouseUp = () => {
      if (isDragging && currentDragSlots.length > 0) {
        if (currentDragSlots.length === 1) {
          // Single click — toggle slot
          const slot = currentDragSlots[0];
          const idx = persistedSlots.findIndex(
            s => s.dateStr === slot.dateStr && s.hour === slot.hour
          );
          const updated = idx !== -1
            ? persistedSlots.filter((_, i) => i !== idx)
            : [...persistedSlots, slot];
          onSlotsChanged(updated);
        } else {
          // Drag — add all slots (additive)
          const merged = [...persistedSlots];
          currentDragSlots.forEach(dragSlot => {
            if (!merged.some(s => s.dateStr === dragSlot.dateStr && s.hour === dragSlot.hour)) {
              merged.push(dragSlot);
            }
          });
          onSlotsChanged(merged);
        }
      }
      setIsDragging(false);
      setDragStartSlot(null);
      setCurrentDragSlots([]);
    };

    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, [isBlockMode, isDragging, currentDragSlots, persistedSlots, onSlotsChanged]);

  const handleSlotMouseDown = useCallback((date, hour) => {
    const dateStr = toDateStr(date);
    setIsDragging(true);
    setDragStartSlot({ dateStr, hour });
    setCurrentDragSlots([{ dateStr, hour }]);
  }, []);

  const handleSlotMouseEnter = useCallback((date, hour) => {
    if (!isDragging || !dragStartSlot) return;
    const dateStr = toDateStr(date);
    if (dateStr !== dragStartSlot.dateStr) return; // Constrain to single column
    const minHour = Math.min(dragStartSlot.hour, hour);
    const maxHour = Math.max(dragStartSlot.hour, hour);
    const rangeSlots = [];
    for (let h = minHour; h <= maxHour; h++) {
      rangeSlots.push({ dateStr, hour: h });
    }
    setCurrentDragSlots(rangeSlots);
  }, [isDragging, dragStartSlot]);

  // Week dates calculation - FIXED to create clean midnight dates
  const getWeekDates = (date) => {
    // Get the year, month, day parts WITHOUT time component
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    // Create a clean date at midnight
    const cleanDate = new Date(year, month, day);
    const dayOfWeek = cleanDate.getDay();

    // Calculate start of week (Sunday)
    const startOfWeek = new Date(year, month, day - dayOfWeek);

    const dates = [];
    for (let i = 0; i < 7; i++) {
      // Create each date as a clean midnight date
      const weekDate = new Date(year, month, day - dayOfWeek + i);
      dates.push(weekDate);
    }
    return dates;
  };

  // Use passed events instead of hook method
  const getEventsForDate = (dateStr) => {
    return events.filter(event => {
      if (!event.start) return false;
      const eventDateStr = event.start.toDateString()
      return eventDateStr === dateStr;
    });
  };

  // Use passed events instead of hook method
  const getAllEventsForDate = (date) => {
    const dateStr = date.toDateString()
    return getEventsForDate(dateStr);
  };

  // Get events for specific time slot (CRITICAL FUNCTION)
  const getEventsForTimeSlot = (date, hour) => {
    const allEvents = getAllEventsForDate(date);

    return allEvents.filter(event => {
      if (!event.start) return false;

      const eventStart = new Date(event.start);
      const eventDate = eventStart.toDateString();
      const slotDate = date.toDateString();
      const eventStartHour = eventStart.getHours();

      // Only return events that START in this specific hour on this date
      return eventDate === slotDate && eventStartHour === hour;
    });
  };

  // Get background events for time slot (CRITICAL FUNCTION)
  const getBackgroundEventsForTimeSlot = (date, hour) => {
    const timeSlotEvents = getEventsForTimeSlot(date, hour);
    return timeSlotEvents.filter(event => event.isBackground || event.is_blocking);
  };

  // Calculate column-based layout for events that start at the same time
  const calculateColumnLayout = (events) => {
    if (events.length === 0) return [];

    const totalEvents = events.length;
    return events.map((event, index) => ({
      ...event,
      columnIndex: index,
      totalColumns: totalEvents,
      widthPercent: 100 / totalEvents,
      leftPercent: (index / totalEvents) * 100
    }));
  };

  // Fixed event positioning - handles multi-hour events properly
  const getEventStyle = (event) => {
    const startHour = event.start.getHours();
    const startMinutes = event.start.getMinutes();
    const endHour = event.end.getHours();
    const endMinutes = event.end.getMinutes();

    // Calculate position within the CURRENT hour slot only
    const minutesFromHourStart = startMinutes;
    const topOffset = (minutesFromHourStart / 60) * 100;

    // Calculate total duration in hours
    const startTime = startHour + startMinutes / 60;
    const endTime = endHour + endMinutes / 60;
    const durationInHours = endTime - startTime;

    // Height spans multiple hour slots if needed
    const height = durationInHours * 100;

    return {
      top: `${topOffset}%`,
      height: `${height}%`
    };
  };

  // Quick action handler (IMPORTANT FUNCTION)
  const handleQuickAction = (event, action) => {
    console.log('Quick action:', action, 'on event:', event.id);
    // TODO: Implement quick action handlers
  };

  const weekDates = getWeekDates(currentDate);
  const hours = Array.from({ length: BUSINESS_HOURS.end - BUSINESS_HOURS.start }, (_, i) => BUSINESS_HOURS.start + i);

  // Use passed loading state instead of hook
  if (loading && !events.length) {
    return (
      <div className="week-view flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Spinner className="size-8 text-blue-500 mx-auto mb-2" />
            <p className="text-slate-400 text-sm">Loading calendar...</p>
          </div>
        </div>
      </div>
    );
  }

  // Use passed error state instead of hook
  if (error) {
    return (
      <div className="week-view flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-400 text-sm">Error loading calendar: {error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-2 px-3 py-1 bg-slate-700 text-slate-300 rounded text-sm hover:bg-slate-600"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Complete JSX using shadcn components with all original functionality
  return (
    <div className="week-view flex flex-col h-full">
      {/* Week header using shadcn component */}
      <WeekViewHeader
        weekDates={weekDates}
        currentDate={currentDate}
      />

      {/* Week grid using shadcn time row components with simplified functionality */}
      <div className="flex-1 overflow-auto">
        {hours.map(hour => (
          <WeekViewTimeRow
            key={hour}
            hour={hour}
            weekDates={weekDates}
            onTimeSlotClick={onTimeSlotClick}
            getEventsForTimeSlot={getEventsForTimeSlot}
            getBackgroundEventsForTimeSlot={getBackgroundEventsForTimeSlot}
            isAdmin={isAdmin}
            availabilityDates={availabilityDates}
            viewingPartnerAvailability={viewingPartnerAvailability}
            isBlockMode={isBlockMode}
            persistedSlots={persistedSlots}
            currentDragSlots={currentDragSlots}
            onSlotMouseDown={handleSlotMouseDown}
            onSlotMouseEnter={handleSlotMouseEnter}
          >
            {/* Column-based event rendering - splits space between events */}
            {(date, hour, dayIndex) => {
              const timeSlotEvents = getEventsForTimeSlot(date, hour);

              if (timeSlotEvents.length === 0) return null;

              // Apply column layout calculation
              const eventsWithLayout = calculateColumnLayout(timeSlotEvents);

              return eventsWithLayout.map((event) => {
                const eventStyle = getEventStyle(event);

                // Calculate z-index based on start time (later events appear on top)
                const startMinutes = event.start.getHours() * 60 + event.start.getMinutes();

                return (
                  <div
                    key={event.id}
                    className="absolute px-0.5"
                    style={{
                      left: `${event.leftPercent}%`,
                      width: `${event.widthPercent}%`,
                      ...eventStyle,
                      zIndex: event.isPreview ? 999 : startMinutes,
                      // Allow clicks to pass through when in creation/block mode
                      pointerEvents: (showEventPanel || isBlockMode) ? 'none' : 'auto'
                    }}
                  >
                    <WeekViewEventCard
                      event={event}
                      onClick={onEventClick}
                      showEventPanel={showEventPanel}
                      isSelected={selectedEventId === event.id}
                    />
                  </div>
                );
              });
            }}
          </WeekViewTimeRow>
        ))}
      </div>
    </div>
  );
};

export default WeekView;